import { Clock, MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { fareharborUrl, FAREHARBOR_LINK_PROPS } from "@/utils/fareharbor";

const IMAGES = {
  "horseback-family":
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_tropical-bookings/artifacts/d2qk70g1_IMG_9076.webp",
  "horseback-beginner":
    "https://images.unsplash.com/photo-1633767979501-6225d151ba70?crop=entropy&cs=srgb&fm=jpg&q=65&w=800",
  "horseback-sunset":
    "https://images.unsplash.com/photo-1696418800218-6f3bb0975d3b?crop=entropy&cs=srgb&fm=jpg&q=65&w=800",
  "snorkel-black-eagle":
    "https://images.unsplash.com/photo-1583364493238-248032147fbd?crop=entropy&cs=srgb&fm=jpg&q=65&w=800",
  "kids-farm":
    "https://customer-assets.emergentagent.com/job_tropical-bookings/artifacts/becjo80d_1.jpg",
  scuba:
    "https://images.unsplash.com/photo-1708649290066-5f617003b93f?crop=entropy&cs=srgb&fm=jpg&q=65&w=800",
};

function StatusBadge({ status, comingSoonLabel, waitlistLabel }) {
  if (status === "coming-soon") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1E8C8C]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#1E8C8C]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#1E8C8C]" />
        {comingSoonLabel}
      </span>
    );
  }
  if (status === "waitlist") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB800]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#9c6f00]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#FFB800]" />
        {waitlistLabel}
      </span>
    );
  }
  return null;
}

export default function Activities() {
  const { t } = useLanguage();
  const a = t.activities;

  return (
    <section
      id="activities"
      className="relative bg-white py-20 md:py-28"
      data-testid="activities-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {/* Section heading */}
        <div className="mb-14 grid items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#FF5533]">
              {a.eyebrow}
            </p>
            <h2
              className="text-4xl leading-[1.02] tracking-tight md:text-6xl"
              style={{ fontFamily: "Fraunces, serif" }}
              data-testid="activities-title"
            >
              {a.title}
            </h2>
          </div>
          <p className="text-base leading-relaxed text-[#4A4A4A] md:col-span-5 md:text-lg">
            {a.sub}
          </p>
        </div>

        {/* Bento grid: 1st card spans 2 cols on desktop; rest 1 col */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-6">
          {a.list.map((act, i) => {
            const isFeatured = i === 0;
            const isUnavailable = act.status !== "live";
            return (
              <article
                key={act.id}
                className={`group relative flex flex-col overflow-hidden rounded-3xl border border-[#E5DACC] bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/10 ${
                  isFeatured ? "md:col-span-4" : "md:col-span-2"
                }`}
                data-testid={`activity-card-${act.id}`}
              >
                {/* Image */}
                <div
                  className={`relative overflow-hidden ${
                    isFeatured ? "h-72 md:h-96" : "h-64"
                  }`}
                >
                  <img
                    src={IMAGES[act.id]}
                    alt={act.name}
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />

                  {/* Top-left tagline + status */}
                  <div className="absolute left-4 top-4 flex items-center gap-2">
                    {act.tagline && (
                      <span className="inline-flex items-center rounded-full bg-white/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#0A0A0A]">
                        {act.tagline}
                      </span>
                    )}
                    <StatusBadge
                      status={act.status}
                      comingSoonLabel={a.coming_soon}
                      waitlistLabel={a.waitlist}
                    />
                  </div>

                  {/* Bottom-left location + duration */}
                  <div className="absolute inset-x-4 bottom-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-white/95">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {act.place}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={13} /> {act.duration}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col gap-4 p-6 md:p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3
                      className="text-2xl leading-tight tracking-tight text-[#0A0A0A] md:text-[1.7rem]"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {act.name}
                    </h3>
                    <span
                      className="shrink-0 text-right text-xl font-semibold tracking-tight text-[#FF5533] md:text-2xl"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {act.price}
                    </span>
                  </div>

                  <p className="text-[15px] leading-relaxed text-[#4A4A4A]">
                    {act.description}
                  </p>

                  {(() => {
                    const isInternal = act.internal === true || (typeof act.href === "string" && act.href.startsWith("/") && !act.href.startsWith("//"));
                    const label =
                      act.status === "coming-soon"
                        ? a.coming_soon
                        : act.status === "waitlist"
                          ? a.waitlist
                          : act.internal
                            ? a.learn_more
                            : a.book;
                    const className = `mt-auto inline-flex items-center justify-between gap-3 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                      isUnavailable
                        ? "border border-[#0A0A0A]/15 bg-white text-[#0A0A0A] hover:border-[#0A0A0A]/30"
                        : "bg-[#0A0A0A] text-white hover:bg-[#FF5533]"
                    }`;
                    const inner = (
                      <>
                        <span>{label}</span>
                        <ArrowUpRight
                          size={16}
                          className="transition-transform duration-300 group-hover:rotate-45"
                        />
                      </>
                    );
                    if (isInternal) {
                      return (
                        <Link
                          to={act.href}
                          data-testid={`activity-book-${act.id}`}
                          className={className}
                        >
                          {inner}
                        </Link>
                      );
                    }
                    const trackedHref = fareharborUrl(act.id, {
                      utm_medium: "activity-card",
                      utm_content: act.id,
                    });
                    return (
                      <a
                        href={trackedHref}
                        {...FAREHARBOR_LINK_PROPS}
                        data-testid={`activity-book-${act.id}`}
                        className={className}
                      >
                        {inner}
                      </a>
                    );
                  })()}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
